/**
 * Template edit / create page.
 *
 * id === "new" → blank profile (create mode).
 * id === any other string → load the profile from the API, then render the
 *   editor pre-populated with the saved data (edit mode).
 */

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { ApiError } from "@/lib/api/client";
import { getProfile } from "@/lib/api/endpoints";
import type { Profile } from "@/lib/mapper/types";
import { ProfileEditor } from "./ProfileEditor";

type LoadedProfile = {
  id: string;
  name: string;
  productType: string;
  config: Profile;
  isDefault: boolean;
};

function emptyProfile(): Profile {
  return {
    productType: "Generic",
    fields: {},
    defaults: {},
    variants: { enabled: false, optionName: "", options: [] },
  };
}

export function ProfileEditorPage() {
  const { id = "new" } = useParams();
  const navigate = useNavigate();

  const isNew = id === "new";

  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<LoadedProfile | null>(null);

  useEffect(() => {
    if (isNew) return;

    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    getProfile(id)
      .then((p) => {
        if (cancelled) return;
        setLoaded(p);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          navigate("/profiles", { replace: true });
          return;
        }
        setLoadError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, isNew, navigate]);

  if (loading) {
    return (
      <div className="flex h-[calc(100vh-3rem)] items-center justify-center text-sm text-slate-500 dark:text-slate-400">
        Loading template…
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex h-[calc(100vh-3rem)] items-center justify-center">
        <div className="rounded border border-red-200 dark:border-red-900/60 bg-red-50 dark:bg-red-950/40 px-6 py-4 text-sm text-red-700 dark:text-red-300">
          {loadError}
        </div>
      </div>
    );
  }

  if (isNew) {
    return (
      <ProfileEditor
        mode="new"
        profileId={null}
        initialName=""
        initialProfile={emptyProfile()}
      />
    );
  }

  // Edit mode — loaded must be set (loading = false, no error, not new)
  if (!loaded) {
    // Shouldn't happen but be safe
    return null;
  }

  return (
    <ProfileEditor
      mode="edit"
      profileId={loaded.id}
      initialName={loaded.name}
      initialProfile={loaded.config ?? emptyProfile()}
    />
  );
}
